/**
 * DPO / DPIA / ROPA — Data Protection Module.
 * Route: /dpo
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { DocumentsLink } from '../components/DocumentsLink';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Scale, Plus, Sparkles, Loader2, Trash2, ShieldAlert, FileText, Globe, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { VerifyReviewsButton } from '../components/VerifyReviewsButton';

const LEGAL_BASIS_LABEL = {
  consent: 'Consentimento (Art. 6(1)(a))',
  contract: 'Execução de contrato (Art. 6(1)(b))',
  legal_obligation: 'Obrigação legal (Art. 6(1)(c))',
  vital_interests: 'Interesses vitais (Art. 6(1)(d))',
  public_task: 'Missão pública (Art. 6(1)(e))',
  legitimate_interests: 'Interesses legítimos (Art. 6(1)(f))',
};

const RISK_META = {
  low: { label: 'Baixo', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  medium: { label: 'Médio', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  high: { label: 'Alto', tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  very_high: { label: 'Muito alto', tone: 'bg-red-100 text-red-800 border-red-300' },
};

const DECISION_META = {
  pending: { label: 'Pendente', tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  approved: { label: 'Aprovado', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  conditional_approval: { label: 'Aprovado c/ condições', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  rejected: { label: 'Rejeitado', tone: 'bg-red-50 text-red-700 border-red-200' },
};

const emptyRopa = {
  name: '', purpose: '', legal_basis: 'contract', legal_basis_details: '',
  data_categories: '', data_subjects: '', recipients: '', retention_period: '',
  international_transfer: false, transfer_countries: '', transfer_safeguards: '',
  security_measures: '', special_categories: false, dpia_required: false,
  controller_name: '', processor_names: '',
};

const emptyDpia = {
  name: '', processing_description: '', linked_ropa_id: '',
  necessity_justification: '', proportionality_assessment: '',
  consulted_stakeholders: '', dpo_opinion: '',
  controller_decision: 'pending', controller_decision_notes: '', review_date: '',
  involves_special_categories: false, involves_automated_decision: false,
  involves_large_scale: false, residual_risk_level: 'medium',
};

export default function Dpo() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'ciso' || user?.role === 'dpo';
  const [tab, setTab] = useState('ropa');
  const [stats, setStats] = useState(null);
  const [ropa, setRopa] = useState([]);
  const [dpia, setDpia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [ropaForm, setRopaForm] = useState(emptyRopa);
  const [ropaDialog, setRopaDialog] = useState(false);
  const [editingRopa, setEditingRopa] = useState(null);
  const [dpiaForm, setDpiaForm] = useState(emptyDpia);
  const [dpiaDialog, setDpiaDialog] = useState(false);
  const [editingDpia, setEditingDpia] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { document.title = 'DPO · NIS2AI'; }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const [s, r, d] = await Promise.all([
          api.get('/dpo/stats'),
          api.get('/dpo/ropa'),
          api.get('/dpo/dpia'),
        ]);
        if (ignore) return;
        setStats(s.data);
        setRopa(r.data.items || []);
        setDpia(d.data.items || []);
      } catch (err) {
        if (!ignore) toast.error(err?.response?.data?.detail || 'Falha ao carregar.');
      } finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, [refresh]);

  const seed = async () => {
    try {
      const { data } = await api.post('/dpo/seed');
      toast.success(`${data.inserted} ROPAs demo criados.`);
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const openRopa = (r) => {
    if (r) {
      setEditingRopa(r);
      setRopaForm({
        ...emptyRopa, ...r,
        data_categories: (r.data_categories || []).join(', '),
        data_subjects: (r.data_subjects || []).join(', '),
        recipients: (r.recipients || []).join(', '),
        transfer_countries: (r.transfer_countries || []).join(', '),
        security_measures: (r.security_measures || []).join(', '),
        processor_names: (r.processor_names || []).join(', '),
      });
    } else {
      setEditingRopa(null);
      setRopaForm(emptyRopa);
    }
    setRopaDialog(true);
  };

  const submitRopa = async () => {
    if (!ropaForm.name || !ropaForm.purpose || !ropaForm.retention_period) {
      toast.error('Nome, finalidade e prazo de conservação são obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...ropaForm,
        data_categories: ropaForm.data_categories.split(',').map(s => s.trim()).filter(Boolean),
        data_subjects: ropaForm.data_subjects.split(',').map(s => s.trim()).filter(Boolean),
        recipients: ropaForm.recipients.split(',').map(s => s.trim()).filter(Boolean),
        transfer_countries: ropaForm.transfer_countries.split(',').map(s => s.trim()).filter(Boolean),
        security_measures: ropaForm.security_measures.split(',').map(s => s.trim()).filter(Boolean),
        processor_names: ropaForm.processor_names.split(',').map(s => s.trim()).filter(Boolean),
      };
      if (editingRopa) await api.patch(`/dpo/ropa/${editingRopa.ropa_id}`, payload);
      else await api.post('/dpo/ropa', payload);
      toast.success(editingRopa ? 'Atualizado.' : 'Criado.');
      setRopaDialog(false);
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
    finally { setSaving(false); }
  };

  const removeRopa = async (r) => {
    if (!window.confirm(`Remover ${r.name}?`)) return;
    try {
      await api.delete(`/dpo/ropa/${r.ropa_id}`);
      toast.success('Removido.');
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const openDpia = (d) => {
    if (d) {
      setEditingDpia(d);
      setDpiaForm({
        ...emptyDpia, ...d,
        consulted_stakeholders: (d.consulted_stakeholders || []).join(', '),
      });
    } else {
      setEditingDpia(null);
      setDpiaForm(emptyDpia);
    }
    setDpiaDialog(true);
  };

  const submitDpia = async () => {
    if (!dpiaForm.name || !dpiaForm.processing_description || !dpiaForm.necessity_justification || !dpiaForm.proportionality_assessment) {
      toast.error('Nome, descrição, necessidade e proporcionalidade são obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...dpiaForm,
        consulted_stakeholders: dpiaForm.consulted_stakeholders.split(',').map(s => s.trim()).filter(Boolean),
        linked_ropa_id: dpiaForm.linked_ropa_id || null,
        review_date: dpiaForm.review_date || null,
        risks_identified: [],
      };
      if (editingDpia) await api.patch(`/dpo/dpia/${editingDpia.dpia_id}`, payload);
      else await api.post('/dpo/dpia', payload);
      toast.success(editingDpia ? 'Atualizado.' : 'Criado.');
      setDpiaDialog(false);
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
    finally { setSaving(false); }
  };

  const removeDpia = async (d) => {
    if (!window.confirm(`Remover ${d.name}?`)) return;
    try {
      await api.delete(`/dpo/dpia/${d.dpia_id}`);
      toast.success('Removido.');
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  return (
    <Layout title="DPO · Proteção de Dados">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="dpo-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <Scale className="h-6 w-6 text-indigo-600" /> DPO · Proteção de Dados
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              <strong>Registo das Atividades de Tratamento (ROPA · Art. 30 RGPD)</strong> e <strong>Avaliações de Impacto (DPIA · Art. 35 RGPD)</strong>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && <VerifyReviewsButton />}
            {canEdit && stats?.ropa_total === 0 && stats?.dpia_total === 0 && (
              <Button variant="outline" onClick={seed} data-testid="dpo-seed-btn"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Popular demo</Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: 'ropa_total', label: 'ROPAs ativos', value: stats?.ropa_total ?? 0, icon: FileText, tone: 'from-indigo-500 to-indigo-600' },
            { key: 'ropa_transfer', label: 'C/ transferência UE', value: stats?.ropa_international_transfer ?? 0, icon: Globe, tone: 'from-sky-500 to-sky-600' },
            { key: 'dpia_pending', label: 'DPIAs pendentes', value: stats?.dpia_pending ?? 0, icon: AlertCircle, tone: 'from-amber-500 to-amber-600' },
            { key: 'dpia_high_risk', label: 'DPIAs risco alto', value: stats?.dpia_high_risk ?? 0, icon: ShieldAlert, tone: 'from-red-500 to-red-600' },
          ].map(k => (
            <Card key={k.key} data-testid={`dpo-kpi-${k.key}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{k.label}</span>
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${k.tone} flex items-center justify-center`}><k.icon className="h-4 w-4 text-white" /></div>
                </div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{k.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="ropa" data-testid="tab-ropa">ROPA ({ropa.length})</TabsTrigger>
            <TabsTrigger value="dpia" data-testid="tab-dpia">DPIA ({dpia.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="ropa">
            <Card>
              <CardContent className="p-0">
                <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                  <div className="text-xs text-slate-500"><strong>Art. 30 RGPD</strong> — Registo obrigatório de todas as atividades de tratamento.</div>
                  {canEdit && <Button size="sm" onClick={() => openRopa(null)} data-testid="ropa-new-btn"><Plus className="h-3.5 w-3.5 mr-1.5" /> Novo ROPA</Button>}
                </div>
                {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /></div> :
                  ropa.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">Sem registos. Use &quot;Popular demo&quot; ou &quot;Novo ROPA&quot;.</div> :
                  <div className="divide-y divide-slate-100">
                    {ropa.map(r => (
                      <div key={r.ropa_id} className="p-4 hover:bg-slate-50" data-testid={`ropa-${r.ropa_id}`}>
                        <div className="flex items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h3 className="font-semibold text-slate-900">{r.name}</h3>
                              <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">{LEGAL_BASIS_LABEL[r.legal_basis]}</Badge>
                              {r.special_categories && <Badge variant="secondary" className="bg-red-50 text-red-700 border-red-200 text-[10px]">Categorias especiais (Art. 9)</Badge>}
                              {r.international_transfer && <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Transferência internacional</Badge>}
                              {r.dpia_required && <Badge variant="secondary" className="bg-orange-50 text-orange-700 border-orange-200 text-[10px]">Requer DPIA</Badge>}
                            </div>
                            <p className="text-sm text-slate-600 mb-1">{r.purpose}</p>
                            <div className="text-xs text-slate-500"><strong>Retenção:</strong> {r.retention_period}</div>
                            {(r.data_categories || []).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {r.data_categories.map((d, i) => <Badge key={i} variant="secondary" className="text-[10px] bg-slate-100 text-slate-600 border-slate-200">{d}</Badge>)}
                              </div>
                            )}
                          </div>
                          {canEdit && (
                            <div className="flex gap-1 flex-shrink-0">
                              <DocumentsLink module="dpo" entityId={r.ropa_id} title={r.name} />
                              <Button variant="ghost" size="sm" onClick={() => openRopa(r)} data-testid={`ropa-edit-${r.ropa_id}`}>Editar</Button>
                              {user?.role === 'admin' && <Button variant="ghost" size="sm" onClick={() => removeRopa(r)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                }
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="dpia">
            <Card>
              <CardContent className="p-0">
                <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                  <div className="text-xs text-slate-500"><strong>Art. 35 RGPD</strong> — Obrigatório quando tratamento apresenta risco elevado (Art. 9, decisões automatizadas, larga escala).</div>
                  {canEdit && <Button size="sm" onClick={() => openDpia(null)} data-testid="dpia-new-btn"><Plus className="h-3.5 w-3.5 mr-1.5" /> Nova DPIA</Button>}
                </div>
                {dpia.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">Sem DPIAs. Registe uma nova avaliação de impacto.</div> :
                  <div className="divide-y divide-slate-100">
                    {dpia.map(d => {
                      const rm = RISK_META[d.residual_risk_level] || {};
                      const dm = DECISION_META[d.controller_decision] || {};
                      return (
                        <div key={d.dpia_id} className="p-4 hover:bg-slate-50" data-testid={`dpia-${d.dpia_id}`}>
                          <div className="flex items-start gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h3 className="font-semibold text-slate-900">{d.name}</h3>
                                <Badge variant="secondary" className={rm.tone}>Risco residual: {rm.label}</Badge>
                                <Badge variant="secondary" className={dm.tone}>{dm.label}</Badge>
                                {d.involves_automated_decision && <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px]">Decisão automatizada</Badge>}
                                {d.involves_large_scale && <Badge variant="secondary" className="bg-orange-50 text-orange-700 border-orange-200 text-[10px]">Larga escala</Badge>}
                              </div>
                              <p className="text-sm text-slate-600 line-clamp-2">{d.processing_description}</p>
                              {d.review_date && <div className="text-xs text-slate-500 mt-1"><strong>Próxima revisão:</strong> {d.review_date}</div>}
                            </div>
                            {canEdit && (
                              <div className="flex gap-1 flex-shrink-0">
                                <Button variant="ghost" size="sm" onClick={() => openDpia(d)} data-testid={`dpia-edit-${d.dpia_id}`}>Editar</Button>
                                {user?.role === 'admin' && <Button variant="ghost" size="sm" onClick={() => removeDpia(d)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                }
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* ROPA dialog */}
      <Dialog open={ropaDialog} onOpenChange={setRopaDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRopa ? `Editar: ${editingRopa.name}` : 'Nova atividade de tratamento (ROPA)'}</DialogTitle>
            <DialogDescription>Registo obrigatório sob o Art. 30 RGPD.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={ropaForm.name} onChange={e => setRopaForm({ ...ropaForm, name: e.target.value })} data-testid="ropa-form-name" /></div>
            <div><Label>Finalidade *</Label><Textarea rows={2} value={ropaForm.purpose} onChange={e => setRopaForm({ ...ropaForm, purpose: e.target.value })} data-testid="ropa-form-purpose" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Base legal *</Label>
                <Select value={ropaForm.legal_basis} onValueChange={v => setRopaForm({ ...ropaForm, legal_basis: v })}>
                  <SelectTrigger data-testid="ropa-form-legal"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(LEGAL_BASIS_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Prazo de conservação *</Label><Input value={ropaForm.retention_period} onChange={e => setRopaForm({ ...ropaForm, retention_period: e.target.value })} placeholder="Ex: 5 anos após fim do contrato" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Categorias de dados (vírgula)</Label><Input value={ropaForm.data_categories} onChange={e => setRopaForm({ ...ropaForm, data_categories: e.target.value })} placeholder="identificação, contacto, saúde" /></div>
              <div><Label>Titulares (vírgula)</Label><Input value={ropaForm.data_subjects} onChange={e => setRopaForm({ ...ropaForm, data_subjects: e.target.value })} placeholder="clientes, colaboradores" /></div>
            </div>
            <div><Label>Destinatários (vírgula)</Label><Input value={ropaForm.recipients} onChange={e => setRopaForm({ ...ropaForm, recipients: e.target.value })} placeholder="Equipa comercial, HubSpot, AWS" /></div>
            <div><Label>Medidas de segurança (vírgula)</Label><Input value={ropaForm.security_measures} onChange={e => setRopaForm({ ...ropaForm, security_measures: e.target.value })} placeholder="TLS, encriptação, MFA, RBAC" /></div>
            <div className="grid grid-cols-3 gap-2">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ropaForm.special_categories} onChange={e => setRopaForm({ ...ropaForm, special_categories: e.target.checked })} /> Cat. especiais (Art. 9)</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ropaForm.international_transfer} onChange={e => setRopaForm({ ...ropaForm, international_transfer: e.target.checked })} /> Transferência internacional</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ropaForm.dpia_required} onChange={e => setRopaForm({ ...ropaForm, dpia_required: e.target.checked })} /> Requer DPIA</label>
            </div>
            {ropaForm.international_transfer && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Países destino (vírgula)</Label><Input value={ropaForm.transfer_countries} onChange={e => setRopaForm({ ...ropaForm, transfer_countries: e.target.value })} /></div>
                <div><Label>Salvaguardas</Label><Input value={ropaForm.transfer_safeguards} onChange={e => setRopaForm({ ...ropaForm, transfer_safeguards: e.target.value })} placeholder="SCC, BCR, adequacy" /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRopaDialog(false)}>Cancelar</Button>
            <Button onClick={submitRopa} disabled={saving} data-testid="ropa-form-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A guardar…</> : editingRopa ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DPIA dialog */}
      <Dialog open={dpiaDialog} onOpenChange={setDpiaDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDpia ? `Editar DPIA: ${editingDpia.name}` : 'Nova DPIA (Art. 35 RGPD)'}</DialogTitle>
            <DialogDescription>Avaliação de Impacto obrigatória para tratamentos de risco elevado.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={dpiaForm.name} onChange={e => setDpiaForm({ ...dpiaForm, name: e.target.value })} data-testid="dpia-form-name" /></div>
            <div><Label>Descrição do tratamento *</Label><Textarea rows={3} value={dpiaForm.processing_description} onChange={e => setDpiaForm({ ...dpiaForm, processing_description: e.target.value })} data-testid="dpia-form-desc" /></div>
            <div>
              <Label>ROPA associado</Label>
              <Select value={dpiaForm.linked_ropa_id || 'none'} onValueChange={v => setDpiaForm({ ...dpiaForm, linked_ropa_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nenhum —</SelectItem>
                  {ropa.map(r => <SelectItem key={r.ropa_id} value={r.ropa_id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Justificação de necessidade *</Label><Textarea rows={2} value={dpiaForm.necessity_justification} onChange={e => setDpiaForm({ ...dpiaForm, necessity_justification: e.target.value })} /></div>
            <div><Label>Avaliação de proporcionalidade *</Label><Textarea rows={2} value={dpiaForm.proportionality_assessment} onChange={e => setDpiaForm({ ...dpiaForm, proportionality_assessment: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-2">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={dpiaForm.involves_special_categories} onChange={e => setDpiaForm({ ...dpiaForm, involves_special_categories: e.target.checked })} /> Categorias especiais</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={dpiaForm.involves_automated_decision} onChange={e => setDpiaForm({ ...dpiaForm, involves_automated_decision: e.target.checked })} /> Decisão automatizada</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={dpiaForm.involves_large_scale} onChange={e => setDpiaForm({ ...dpiaForm, involves_large_scale: e.target.checked })} /> Larga escala</label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Risco residual</Label>
                <Select value={dpiaForm.residual_risk_level} onValueChange={v => setDpiaForm({ ...dpiaForm, residual_risk_level: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(RISK_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Decisão do responsável</Label>
                <Select value={dpiaForm.controller_decision} onValueChange={v => setDpiaForm({ ...dpiaForm, controller_decision: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(DECISION_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Consultados (vírgula)</Label><Input value={dpiaForm.consulted_stakeholders} onChange={e => setDpiaForm({ ...dpiaForm, consulted_stakeholders: e.target.value })} placeholder="DPO, IT-Sec, Legal" /></div>
            <div><Label>Parecer do DPO</Label><Textarea rows={2} value={dpiaForm.dpo_opinion} onChange={e => setDpiaForm({ ...dpiaForm, dpo_opinion: e.target.value })} /></div>
            <div><Label>Data próxima revisão</Label><Input type="date" value={dpiaForm.review_date} onChange={e => setDpiaForm({ ...dpiaForm, review_date: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDpiaDialog(false)}>Cancelar</Button>
            <Button onClick={submitDpia} disabled={saving} data-testid="dpia-form-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A guardar…</> : editingDpia ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
